/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/** Tailwind class: offset in-page anchors by the live sticky header height. */
export const STICKY_HEADER_SCROLL_MARGIN_CLASS = 'scroll-mt-[var(--header-height,0px)]';

/** Tailwind class: block height = viewport minus sticky header (`--header-height` on `:root`). */
export const VIEWPORT_BELOW_STICKY_HEADER_HEIGHT_CLASS =
    'h-[calc(100dvh-var(--header-height,0px))]';

export function readStickyHeaderHeightPx(): number {
    if (typeof document === 'undefined') {
        return 0;
    }

    const raw = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : 0;
}

function readScrollMarginTopPx(element: HTMLElement): number {
    return Number.parseFloat(getComputedStyle(element).scrollMarginTop) || 0;
}

/** Scroll offset so the target clears the sticky site header. */
export function readSectionScrollOffsetPx(element: HTMLElement): number {
    const scrollMargin = readScrollMarginTopPx(element);
    const headerHeight = readStickyHeaderHeightPx();
    return Math.max(scrollMargin, headerHeight);
}

export function getSectionScrollTop(element: HTMLElement, scrollY = window.scrollY): number {
    return element.getBoundingClientRect().top + scrollY - readSectionScrollOffsetPx(element);
}
